import { URLS } from '../navigation/navigation.data';
import { SERVICES } from './product.data';
import { ProductFeature, ProductPictures, ShowcaseConfig } from './product-features.data';

/** The services' pictures: the 3D renders the service cards always carried, cut to their own edge
 *  (svc-<key>.webp 480px tall, -large 800px). */
export const SERVICE_PICTURES: ProductPictures = { dir: './assets/product-lines/services/', prefix: 'svc-' };

const BRAND_MARK = './assets/brand/min/logo/brand-logo.min.webp';

// One service a slide (owner 2026-10-11: Our Services as a box like the others, after the blog); the
// chips switch it. The left side is the service's own (its name from product.data.ts SERVICES, a short lead
// and text below), the headline over the picture says it in a line; no fact chips.
const SLIDES: ProductFeature[] = [
  {
    key: 'software-consultancy', chip: 'Consultancy',
    accent: 'Custom Software,', rest: 'End to End', layout: 'one',
    line: 'Our engineers architect, build, test and ship software shaped around your business',
    description: 'Custom software development, end to end.',
    scenes: [ { key: 'software-consultancy', name: 'Software Consultancy', alt: 'A laptop with the Codigrate mark on its screen', width: 568, height: 480 } ],
  },
  {
    key: 'software-integration', chip: 'Integration',
    accent: 'Your Tools,', rest: 'Working Together', layout: 'one',
    line: 'Third-party API integrations and workflow automation, tested and shipped',
    description: 'Software integration.',
    scenes: [ { key: 'software-integration', name: 'Software Integration', alt: 'A globe with a line of light circling it', width: 490, height: 480 } ],
  },
  {
    key: 'payment-gateway-integration', chip: 'Payments',
    accent: 'Payments', rest: 'That Just Work', layout: 'one',
    line: 'Stripe, PayPal and more, with secure checkout, subscriptions and billing',
    description: 'Payment gateway integration.',
    scenes: [ { key: 'payment-gateway-integration', name: 'Payment Gateway Integration', alt: 'A payment card wired to its chip', width: 422, height: 480 } ],
  },
  {
    key: 'mobile-app-development', chip: 'Mobile Apps',
    accent: 'Mobile Apps,', rest: 'Built and Connected', layout: 'one',
    line: 'Custom iPhone and Android apps, built and integrated with your backend',
    description: 'Mobile app development.',
    scenes: [ { key: 'mobile-app-development', name: 'Mobile App Development', alt: 'A phone with the Codigrate mark on its screen', width: 294, height: 480 } ],
  },
  {
    key: 'talent-discovery-solutions', chip: 'Talent Discovery',
    accent: 'The Right Talent,', rest: 'in Seconds', layout: 'one',
    line: 'AI-assisted sourcing across millions of profiles, ranked and fed into your hiring pipeline',
    description: 'Talent discovery solutions.',
    scenes: [ { key: 'talent-discovery-solutions', name: 'Talent Discovery Solutions', alt: 'A server stack linked to the tools of a hiring pipeline', width: 464, height: 480 } ],
  },
];

// the left side's short lead and text per service (owner 2026-10-11: the lead stays one short line, the text a full paragraph)
const COPY: Record<string, { lead: string; text: string }> = {
  'software-consultancy': {
    lead: 'Architecture, development and delivery',
    text: 'Our engineers design, build, test and ship custom software around the way your business works. We start from your goals, shape the architecture with you, deliver in small reviewed steps and stay on for the releases that follow, while our designers give the product an interface of its own.',
  },
  'software-integration': {
    lead: 'Third-party APIs and automated workflows',
    text: 'From payment providers to social platforms, we connect the services your business relies on so data moves between them without manual work. Our team handles the hard parts of every third-party integration: the design, the testing, the debugging and a careful rollout into production.',
  },
  'payment-gateway-integration': {
    lead: 'Stripe, PayPal and more, done safely',
    text: 'Checkout, subscriptions, invoices, billing and reconciliation wired into your product with secure, well tested payment flows. Our experience is shaped around Stripe and PayPal, from tax and receipts to webhooks and refunds, so every payment lands where it should.',
  },
  'mobile-app-development': {
    lead: 'iPhone and Android apps that ship',
    text: 'Custom iPhone and Android apps designed, built and connected to your servers, from the first screen to the store listing. Our developers integrate the app with your backend and services, and our designers create an interface that feels native and unmistakably yours.',
  },
  'talent-discovery-solutions': {
    lead: 'AI-assisted hiring at scale',
    text: 'Search millions of smartly selected profiles and rank the best fits by skill, location and experience in a matter of seconds. The shortlist flows straight into your hiring pipeline, and the system learns from your feedback, so every search brings the right candidates faster.',
  },
};

const SERVICES_SLIDES: ProductFeature[] = SLIDES.map( slide => {
  const service = ( SERVICES[ 0 ]?.products ?? [] ).find( p => p.key === slide.key );
  // the title's icon is the service's own picture, the one shown big on the right
  const icon = `${ SERVICE_PICTURES.dir }${ SERVICE_PICTURES.prefix }${ slide.key }.webp`;
  return { ...slide, title: service?.name, icon, ...COPY[ slide.key ] };
} );

/** The home's Our Services box (product-showcase.component.ts). */
export const SERVICES_SHOWCASE: ShowcaseConfig = {
  key: 'services',
  shape: 'scene',
  eyebrowLogo: BRAND_MARK,
  eyebrow: 'Our Services',
  icon: BRAND_MARK,
  title: 'Our Services',
  lead: 'Software built, integrated and shipped around your business',
  text: 'The team behind the themes and apps on this site also builds for you: custom software, integrations, payments, mobile apps and talent discovery.',
  facts: [],
  link: URLS.contactUs,
  cta: 'Contact Us',
  soon: '',
  pickerLabel: 'Services',
  pictures: SERVICE_PICTURES,
  slides: SERVICES_SLIDES,
};
